/**
 * <cart-usps-slider> — cart drawer USP row (same logic as product-usps-slider).
 * 1–2 items: static layout. 3+ items: duplicate wrappers + Web Animations / Safari CSS.
 */
import { responsive } from '@theme/responsive';

const USP_STATIC_MAX = 2;

function debounce(fn, delay) {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), delay);
  };
}

class CartUspsSlider extends HTMLElement {
  #resizeCleanup = null;
  #visibilityObserver = null;
  #drawerOpenedHandler = null;
  #cartUpdateHandler = null;
  #cartRenderHandler = null;
  #pauseHandler = null;
  #playHandler = null;

  connectedCallback() {
    this.transitionSpeedSliding = this.getAttribute('data-settings');
    this.enableAnimation = this.getAttribute('data-enable-animation') === 'true';
    this.animWrapper = this.querySelector('.pm__usps-slider--anim-wrapper');
    this.original = this.querySelector('.pm__usps-slider--duplicate-wrapper');
    this.hasInitialized = false;
    this.currentAnimation = null;
    this.mobile = null;
    this.isSafari = false;
    this.safariStyleSheet = null;
    this.spaceDivWidth = 0;

    if (!this.animWrapper || !this.original) return;

    const itemCount = this.original.children.length;
    this.#bindEvents();

    if (itemCount <= USP_STATIC_MAX) {
      this.#applyStaticLayout(itemCount);
      this.hasInitialized = true;
      return;
    }

    if (this.enableAnimation && this.transitionSpeedSliding) {
      this.#visibilityObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && !this.hasInitialized) {
              this.hasInitialized = true;
              setTimeout(() => {
                this.handleBreakpointChange();
              }, 100);
            } else if (!entry.isIntersecting && this.hasInitialized) {
              if (this.isSafari && this.animWrapper) {
                this.animWrapper.style.animationPlayState = 'paused';
                this.animWrapper.style.webkitAnimationPlayState = 'paused';
              } else if (this.currentAnimation) {
                this.currentAnimation.pause();
              }
            } else if (entry.isIntersecting && this.hasInitialized) {
              if (this.isSafari && this.animWrapper) {
                this.animWrapper.style.animationPlayState = 'running';
                this.animWrapper.style.webkitAnimationPlayState = 'running';
              } else if (this.currentAnimation) {
                this.currentAnimation.play();
              }
            }
          });
        },
        { threshold: 0.01 }
      );

      this.#visibilityObserver.observe(this);
    }

    this.#drawerOpenedHandler = () => {
      const drawer = this.closest('cart-drawer');
      if (!drawer?.isOpen) return;
      setTimeout(() => this.reinitialize(), 100);
    };
    document.addEventListener('drawer:opened', this.#drawerOpenedHandler);
  }

  disconnectedCallback() {
    this.stopAnimation();

    this.#visibilityObserver?.disconnect();
    this.#visibilityObserver = null;

    if (this.#cartUpdateHandler) {
      document.removeEventListener('cart:updated', this.#cartUpdateHandler);
    }
    if (this.#cartRenderHandler) {
      document.removeEventListener('cart:render', this.#cartRenderHandler);
    }

    if (this.#pauseHandler) {
      this.removeEventListener('mouseenter', this.#pauseHandler);
      this.removeEventListener('mouseleave', this.#playHandler);
    }

    if (this.safariStyleSheet) {
      this.safariStyleSheet.remove();
      this.safariStyleSheet = null;
    }

    this.#resizeCleanup?.removeResizeListener();
    this.#resizeCleanup = null;

    if (this.#drawerOpenedHandler) {
      document.removeEventListener('drawer:opened', this.#drawerOpenedHandler);
      this.#drawerOpenedHandler = null;
    }

    this.hasInitialized = false;
  }

  #bindEvents() {
    if (this.enableAnimation) {
      const isIOS =
        /iPad|iPhone|iPod/.test(navigator.userAgent) ||
        (navigator.maxTouchPoints > 1 && /Macintosh/.test(navigator.userAgent));
      this.isSafari =
        isIOS || /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

      const debouncedFunc = debounce(this.handleBreakpointChange.bind(this), 100);
      this.#resizeCleanup = responsive.addResizeListener(this, debouncedFunc);

      this.#cartUpdateHandler = () => {
        if (this.isSafari && this.animWrapper) {
          this.animWrapper.style.animationPlayState = 'paused';
          this.animWrapper.style.webkitAnimationPlayState = 'paused';
        } else if (this.currentAnimation) {
          this.currentAnimation.pause();
        }
      };

      this.#cartRenderHandler = () => {
        requestAnimationFrame(() => {
          if (this.offsetParent === null) return;

          const itemCount = this.original?.children.length || 0;
          if (itemCount <= USP_STATIC_MAX) {
            this.#applyStaticLayout(itemCount);
            return;
          }

          if (this.isSafari && this.animWrapper) {
            this.animWrapper.style.animationPlayState = 'running';
            this.animWrapper.style.webkitAnimationPlayState = 'running';
          } else if (this.currentAnimation) {
            this.currentAnimation.play();
          } else if (this.hasInitialized && !this.isSafari) {
            this.startAnimating();
          }
        });
      };

      document.addEventListener('cart:updated', this.#cartUpdateHandler);
      document.addEventListener('cart:render', this.#cartRenderHandler);
    } else {
      this.#applyStaticLayout(this.original.children.length || 1);
    }
  }

  handleBreakpointChange() {
    const isDesktop = responsive.matchesBreakpoint('lap_and_up');
    const newMobileState = !isDesktop;
    const breakpointChanged = this.mobile !== newMobileState;
    const needsInit = !this.hasInitialized || breakpointChanged;

    if (!needsInit) {
      return;
    }

    this.mobile = newMobileState;

    if (isDesktop) {
      if (this.transitionSpeedSliding) {
        this.duplicateBlockWrappers();
      } else {
        Array.from(this.original.children).forEach((block) => {
          block.style.display = 'flex';
        });
      }
    } else if (this.transitionSpeedSliding) {
      this.duplicateBlockWrappers();
    } else {
      this.original.style.justifyContent = 'center';
    }
  }

  resetWrapper() {
    while (this.animWrapper.children.length > 1) {
      this.animWrapper.removeChild(this.animWrapper.lastChild);
    }
  }

  duplicateBlockWrappers() {
    this.resetWrapper();

    const itemCount = this.original.children.length;
    this.dataset.uspCount = String(itemCount);

    if (itemCount <= USP_STATIC_MAX) {
      this.#applyStaticLayout(itemCount);
      return;
    }

    this.dataset.uspLayout = 'marquee';

    const originalWidth = this.original.offsetWidth;

    if (originalWidth === 0) {
      return;
    }

    if (itemCount === 1) {
      this.original.style.width = '100%';
      this.animWrapper.style.width = '100%';
      this.animWrapper.style.position = 'relative';
      this.original.style.justifyContent = 'center';

      const computedStyle = window.getComputedStyle(this);
      const paddingTop = Number.parseFloat(computedStyle.paddingTop) || 0;
      const paddingBottom = Number.parseFloat(computedStyle.paddingBottom) || 0;
      const totalPadding = paddingTop + paddingBottom;

      this.style.height = `${this.animWrapper.offsetHeight + totalPadding}px`;
      return;
    }

    if (this.original.children.length >= 2) {
      this.spaceDivWidth = this.getDistanceBetweenBlocks(
        this.original.children[0],
        this.original.children[1]
      );
    } else {
      this.spaceDivWidth = 0;
    }

    const duplicateCount = itemCount <= 3 ? 5 : 2;
    const wrapperWidth =
      (duplicateCount + 1) * originalWidth + duplicateCount * this.spaceDivWidth;

    this.animWrapper.style.width = `${wrapperWidth}px`;
    this.animWrapper.style.position = 'absolute';

    this.original.style.width = `${originalWidth}px`;
    this.original.style.flexShrink = '0';

    for (let i = 0; i < duplicateCount; i++) {
      const spaceDiv = document.createElement('div');
      spaceDiv.style.width = `${this.spaceDivWidth}px`;
      spaceDiv.style.flexShrink = '0';
      this.animWrapper.appendChild(spaceDiv);

      const duplicate = this.original.cloneNode(true);
      duplicate.style.width = `${originalWidth}px`;
      duplicate.style.flexShrink = '0';
      this.animWrapper.appendChild(duplicate);
    }

    const computedStyle = window.getComputedStyle(this);
    const paddingTop = Number.parseFloat(computedStyle.paddingTop) || 0;
    const paddingBottom = Number.parseFloat(computedStyle.paddingBottom) || 0;
    const totalPadding = paddingTop + paddingBottom;

    this.style.height = `${this.animWrapper.offsetHeight + totalPadding}px`;

    setTimeout(() => {
      this.startAnimating();
    }, 100);
  }

  reinitialize() {
    const itemCount = this.original?.children.length || 0;

    if (itemCount <= USP_STATIC_MAX) {
      this.#applyStaticLayout(itemCount);
      this.hasInitialized = true;
      return;
    }

    if (this.enableAnimation && this.transitionSpeedSliding) {
      this.duplicateBlockWrappers();
    }
  }

  startAnimating() {
    if (this.currentAnimation) {
      this.currentAnimation.cancel();
      this.currentAnimation = null;
    }

    if (this.safariStyleSheet) {
      this.safariStyleSheet.remove();
      this.safariStyleSheet = null;
    }

    if (this.isSafari && this.animWrapper) {
      this.animWrapper.style.animation = 'none';
      this.animWrapper.style.webkitAnimation = 'none';
    }

    const originalWidth = this.original.offsetWidth;
    const singleSetWidth = originalWidth + this.spaceDivWidth;
    const translateStart = 0;
    const translateEnd = -singleSetWidth;
    const duration = Number.parseInt(this.transitionSpeedSliding, 10) * 1000;

    this.animWrapper.style.willChange = 'transform';
    this.animWrapper.style.backfaceVisibility = 'hidden';
    this.animWrapper.style.webkitBackfaceVisibility = 'hidden';

    if (this.isSafari) {
      const animationName = `cart-usps-slide-${Date.now()}`;

      this.safariStyleSheet = document.createElement('style');
      this.safariStyleSheet.textContent = `
        @-webkit-keyframes ${animationName} {
          from { -webkit-transform: translate3d(${translateStart}px, -50%, 0); transform: translate3d(${translateStart}px, -50%, 0); }
          to { -webkit-transform: translate3d(${translateEnd}px, -50%, 0); transform: translate3d(${translateEnd}px, -50%, 0); }
        }
        @keyframes ${animationName} {
          from { transform: translate3d(${translateStart}px, -50%, 0); }
          to { transform: translate3d(${translateEnd}px, -50%, 0); }
        }
      `;
      document.head.appendChild(this.safariStyleSheet);

      this.animWrapper.style.animation = `${animationName} ${duration}ms linear infinite`;
      this.animWrapper.style.webkitAnimation = `${animationName} ${duration}ms linear infinite`;

      if (!this.#pauseHandler) {
        this.#pauseHandler = () => {
          this.animWrapper.style.animationPlayState = 'paused';
          this.animWrapper.style.webkitAnimationPlayState = 'paused';
        };
        this.#playHandler = () => {
          this.animWrapper.style.animationPlayState = 'running';
          this.animWrapper.style.webkitAnimationPlayState = 'running';
        };

        this.addEventListener('mouseenter', this.#pauseHandler);
        this.addEventListener('mouseleave', this.#playHandler);
      }
    } else {
      this.animWrapper.style.transform = `translate3d(${translateStart}px, -50%, 0)`;

      this.currentAnimation = this.animWrapper.animate(
        [
          { transform: `translate3d(${translateStart}px, -50%, 0)` },
          { transform: `translate3d(${translateEnd}px, -50%, 0)` },
        ],
        {
          duration,
          iterations: Infinity,
          easing: 'linear',
        }
      );

      if (!this.#pauseHandler) {
        this.#pauseHandler = () => {
          if (this.currentAnimation) this.currentAnimation.pause();
        };
        this.#playHandler = () => {
          if (this.currentAnimation) this.currentAnimation.play();
        };

        this.addEventListener('mouseenter', this.#pauseHandler);
        this.addEventListener('mouseleave', this.#playHandler);
      }
    }
  }

  stopAnimation() {
    if (this.currentAnimation) {
      this.currentAnimation.cancel();
      this.currentAnimation = null;
    }

    if (this.isSafari && this.animWrapper) {
      this.animWrapper.style.animation = 'none';
      this.animWrapper.style.webkitAnimation = 'none';
    }
  }

  getDistanceBetweenBlocks(blockOne, blockTwo) {
    return blockTwo.getBoundingClientRect().left - blockOne.getBoundingClientRect().right;
  }

  #applyStaticLayout(itemCount) {
    this.dataset.uspLayout = 'static';
    this.dataset.uspCount = String(itemCount);
    this.stopAnimation();
    this.resetWrapper();

    this.style.height = '';
    this.animWrapper.style.width = '100%';
    this.animWrapper.style.position = 'relative';
    this.animWrapper.style.transform = 'none';
    this.original.style.width = '100%';
    this.original.style.flexShrink = '';
    this.original.style.justifyContent = 'flex-start';
    this.original.style.gap = '0';

    Array.from(this.original.children).forEach((block, index) => {
      block.style.display = 'flex';
      if (itemCount === 1) {
        block.style.flex = '1 1 100%';
        block.style.maxWidth = '100%';
        block.style.width = '100%';
      } else if (itemCount === 2) {
        block.style.flex = '1 1 50%';
        block.style.maxWidth = '50%';
        block.style.width = '50%';
      } else {
        block.style.flex = '';
        block.style.maxWidth = '';
        block.style.width = '';
      }

      if (index > 0 && itemCount === 2) {
        block.style.boxSizing = 'border-box';
      }
    });
  }
}

if (!customElements.get('cart-usps-slider')) {
  customElements.define('cart-usps-slider', CartUspsSlider);
}
